import React, { useState, useEffect } from 'react';
import { Clock, Settings, Save, RefreshCw, Users, Shield, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight, X, Trash2, History, Calendar, CalendarClock, Lock, Unlock, Plus, Edit2, Search, Sun, Moon, ArrowRight } from 'lucide-react';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import CarteReglages from './parametrage/CarteReglages';
import ChampSelect from './ui/ChampSelect';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import ConfirmDialog from './ConfirmDialog';
import { Toast } from "../components/ui/Toast";
import { getAuthHeaders } from '../services/api';


const SessionConfigDashboard = ({ onBackToDashboard }) => {
  const [config, setConfig] = useState({
  DEFAULT_DURATION: 4 * 60 * 60 * 1000,       // ✅ 4 heures
  EXTENSION_DURATION: 2 * 60 * 60 * 1000,     // ✅ 2 heures  
  MAX_DAILY_DURATION: 8 * 60 * 60 * 1000,     // ✅ 8 heures max
  MAX_EXTENSIONS_PER_DAY: 2,
  ALERT_TIMES: {
    WARNING: 15 * 60 * 1000,    // ✅ 15 minutes
    URGENT: 5 * 60 * 1000,      // ✅ 5 minutes
    CRITICAL: 2 * 60 * 1000     // ✅ 2 minutes
  }
});

  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState({ sessions: [], adminActions: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // L'onglet ouvert remplace les deux booleens `showHistory` et
  // `showTimeRestrictions` : ils pilotaient des replis, ils pilotent
  // maintenant des onglets. Le rafraichissement de l'historique en depend —
  // sans cet etat, il ne se serait plus jamais declenche.
  const [ongletActif, setOngletActif] = useState('durees');
  const historiqueVisible = ongletActif === 'historique';
  
  // États pour la pagination des sessions
  const [currentPage, setCurrentPage] = useState(1);
  const [sessionsPerPage] = useState(5);
  const [countdownIntervals, setCountdownIntervals] = useState({});

  // États pour l'historique
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPerPage] = useState(10);
  const [historyType, setHistoryType] = useState('all');

  // États pour les dialogues de confirmation
  const [confirmDialogs, setConfirmDialogs] = useState({
    deleteSession: { isOpen: false, sessionId: null },
    clearHistory: { isOpen: false },
    deleteProfile: { isOpen: false, profileId: null }
  });

  // États pour les toasts
  const [toasts, setToasts] = useState([]);

  // 🆕 États pour les plages horaires
  const [timeRestrictionTab, setTimeRestrictionTab] = useState('profiles'); // profiles ou assignments
  const [sessionProfiles, setSessionProfiles] = useState([
    {
      id: 'permanent',
      name: 'Accès Permanent',
      description: 'Connexion autorisée 24/7 sans restriction',
      type: 'permanent',
      isDefault: true,
      assignedUsers: 0
    },
    {
      id: 'daily-standard',
      name: 'Horaires Standard',
      description: 'Lundi-Vendredi, 6h00-16h00',
      type: 'daily',
      startTime: '06:00',
      endTime: '16:00',
      daysOfWeek: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'],
      isDefault: true,
      assignedUsers: 0
    }
  ]);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [newProfile, setNewProfile] = useState({
    name: '',
    description: '',
    type: 'daily',
    startTime: '08:00',
    endTime: '17:00',
    daysOfWeek: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven']
  });
  const [users, setUsers] = useState([
    { id: 1, identifiant: 'user1', nom: 'Dupont', prenom: 'Jean', profileId: 'permanent' },
    { id: 2, identifiant: 'user2', nom: 'Martin', prenom: 'Marie', profileId: 'daily-standard' }
  ]);
  const [userSearch, setUserSearch] = useState('');
  const [violations, setViolations] = useState([
    {
      id: 1,
      user: 'user1',
      reason: 'Tentative de connexion hors horaires autorisées (17:30)',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      ip: '192.168.1.105',
      blocked: true
    }
  ]);

  // ✅ CORRECTION : Définir removeToast AVANT addToast
  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Fonction pour ajouter un toast
  const addToast = (type, message) => {
    const newToast = {
      id: Date.now() + Math.random(),
      type,
      message,
      timestamp: Date.now()
    };
    setToasts(prev => [...prev, newToast]);
    
    // Auto-suppression après 5 secondes
    setTimeout(() => {
      removeToast(newToast.id);
    }, 5000);
  };

  // Convertir millisecondes en minutes pour l'affichage
  const msToMinutes = (ms) => Math.floor(ms / (60 * 1000));
  const minutesToMs = (minutes) => minutes * 60 * 1000;

  // Fonctions pour gérer les durées avec unités flexibles
  const formatDurationForInput = (ms) => {
    const totalMinutes = msToMinutes(ms);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0 && minutes > 0) {
      return { value: `${hours}:${minutes.toString().padStart(2, '0')}`, unit: 'hours' };
    } else if (hours > 0) {
      return { value: hours.toString(), unit: 'hours' };
    } else {
      return { value: totalMinutes.toString(), unit: 'minutes' };
    }
  };

  const parseDurationInput = (inputValue, unit) => {
    if (!inputValue || inputValue.toString().trim() === '') return 0;
    
    const cleanValue = inputValue.toString().trim();
    
    if (unit === 'hours') {
      // Support pour format "2:30" (2h30) ou "2" (2h00) ou "1.5" (1h30)
      if (cleanValue.includes(':')) {
        const parts = cleanValue.split(':');
        const hours = parseInt(parts[0]) || 0;
        const minutes = parseInt(parts[1]) || 0;
        
        // Validation des minutes (0-59)
        if (minutes >= 60) {
          console.warn('Minutes ne peuvent pas dépasser 59');
          return hours * 60;
        }
        
        return (hours * 60) + minutes;
      } else {
        // Format decimal (1.5 = 1h30) ou entier (2 = 2h00)
        const hours = parseFloat(cleanValue) || 0;
        return Math.round(hours * 60);
      }
    } else {
      // Pour les minutes, accepter seulement des entiers
      return parseInt(cleanValue) || 0;
    }
  };

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h${mins}min` : `${hours}h`;
  };

  // Fonction pour formater le temps restant avec plus de détails
  const formatTimeRemaining = (minutes) => {
    if (minutes <= 0) return { text: 'Expiré', color: 'text-red-600', urgency: 'expired' };
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (minutes <= 5) {
      return { 
        text: `${minutes}min`, 
        color: 'text-red-600', 
        urgency: 'critical',
        description: 'Session expire bientôt'
      };
    } else if (minutes <= 15) {
      return { 
        text: hours > 0 ? `${hours}h${mins}min` : `${minutes}min`, 
        color: 'text-orange-600', 
        urgency: 'warning',
        description: 'Session expire prochainement'
      };
    } else {
      return { 
        text: hours > 0 ? `${hours}h${mins}min` : `${minutes}min`, 
        color: 'text-green-600', 
        urgency: 'normal',
        description: 'Session active'
      };
    }
  };

  // Formater les dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculs pour la pagination
  const totalPages = Math.ceil(sessions.length / sessionsPerPage);
  const startIndex = (currentPage - 1) * sessionsPerPage;
  const endIndex = startIndex + sessionsPerPage;
  const currentSessions = sessions.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Pagination pour l'historique
  const allHistoryItems = [...history.sessions, ...history.adminActions].sort((a, b) => 
    new Date(b.connection_time || b.created_at) - new Date(a.connection_time || a.created_at)
  );
  const filteredHistory = historyType === 'all' ? allHistoryItems : 
    historyType === 'sessions' ? history.sessions : history.adminActions;
  
  const historyTotalPages = Math.ceil(filteredHistory.length / historyPerPage);
  const historyStartIndex = (historyPage - 1) * historyPerPage;
  const currentHistory = filteredHistory.slice(historyStartIndex, historyStartIndex + historyPerPage);


// Charger la configuration actuelle
  useEffect(() => {
    loadSessionConfig();
    loadActiveSessions();
    loadSessionStats();
    if (historiqueVisible) {
      loadSessionHistory();
    }
    
    // Nettoyer les intervalles au démontage
    return () => {
      Object.values(countdownIntervals).forEach(clearInterval);
    };
  }, [historiqueVisible]);
  
  // Démarrer les comptes à rebours pour les sessions
  useEffect(() => {
    // Nettoyer les anciens intervalles
    Object.values(countdownIntervals).forEach(clearInterval);
    
    const newIntervals = {};
    sessions.forEach(session => {
      if (session.timeLeftMinutes > 0) {
        newIntervals[session.sessionId] = setInterval(() => {
          setSessions(prevSessions => 
            prevSessions.map(s => 
              s.sessionId === session.sessionId 
                ? { ...s, timeLeftMinutes: Math.max(0, s.timeLeftMinutes - 1) }
                : s
            )
          );
        }, 60000); // Mise à jour chaque minute
      }
    });
    
    setCountdownIntervals(newIntervals);
  }, [sessions.length]);


const loadSessionConfig = async () => {
  try {
    const response = await fetch('/api/admin/sessions/config', {
      headers: getAuthHeaders()
    });
    if (response.ok) {
      const data = await response.json();
      setConfig(data.config || config);
    }
  } catch (error) {
    console.error('Erreur chargement config:', error);
    addToast('error', 'Erreur lors du chargement de la configuration');
  }
};




 const loadActiveSessions = async () => {
  try {
    setLoading(true);
    const response = await fetch('/api/admin/sessions/active', {
      headers: getAuthHeaders()
    });
    if (response.ok) {
      const data = await response.json();
      setSessions(data.sessions || []);
    }
  } catch (error) {
    console.error('Erreur chargement sessions:', error);
    addToast('error', 'Erreur lors du chargement des sessions');
  } finally {
    setLoading(false);
  }
};


const loadSessionStats = async () => {
  try {
    const response = await fetch('/api/admin/sessions/stats', {
      headers: getAuthHeaders()
    });
    if (response.ok) {
      const data = await response.json();
      setStats(data.statistics);
    }
  } catch (error) {
    console.error('Erreur chargement stats:', error);
    addToast('error', 'Erreur lors du chargement des statistiques');
  }
};



const loadSessionHistory = async () => {
  try {
    const response = await fetch(`/api/admin/sessions/history?type=${historyType}&limit=100`, {
      headers: getAuthHeaders()
    });
    if (response.ok) {
      const data = await response.json();
      setHistory(data.history || { sessions: [], adminActions: [] });
    }
  } catch (error) {
    console.error('Erreur chargement historique:', error);
    addToast('error', 'Erreur lors du chargement de l\'historique');
  }
};



  // Fonctions pour les dialogues de confirmation
  const openDeleteSessionConfirm = (sessionId) => {
    setConfirmDialogs(prev => ({
      ...prev,
      deleteSession: { isOpen: true, sessionId }
    }));
  };

  const closeDeleteSessionConfirm = () => {
    setConfirmDialogs(prev => ({
      ...prev,
      deleteSession: { isOpen: false, sessionId: null }
    }));
  };

  const openClearHistoryConfirm = () => {
    setConfirmDialogs(prev => ({
      ...prev,
      clearHistory: { isOpen: true }
    }));
  };

  const closeClearHistoryConfirm = () => {
    setConfirmDialogs(prev => ({
      ...prev,
      clearHistory: { isOpen: false }
    }));
  };




const handleDeleteSession = async () => {
  const sessionId = confirmDialogs.deleteSession.sessionId;
  if (!sessionId) return;

  try {
    const response = await fetch(`/api/admin/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (response.ok) {
      addToast('success', 'Session supprimée avec succès');
      loadActiveSessions();
      loadSessionStats();
      if (historiqueVisible) {
        loadSessionHistory();
      }
    } else {
      const error = await response.json();
      addToast('error', error.message || 'Erreur lors de la suppression');
    }
  } catch (error) {
    addToast('error', 'Erreur réseau lors de la suppression');
  }
  closeDeleteSessionConfirm();
};




const handleClearHistory = async () => {
  try {
    const response = await fetch('/api/admin/sessions/history', {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ olderThanDays: 0, type: 'all' })
    });

    if (response.ok) {
      addToast('success', 'Historique vidé avec succès');
      loadSessionHistory();
    } else {
      const error = await response.json();
      addToast('error', error.message || 'Erreur lors du nettoyage');
    }
  } catch (error) {
    addToast('error', 'Erreur réseau lors du nettoyage');
  }
  closeClearHistoryConfirm();
};




  const saveConfiguration = async () => {
  try {
    setSaving(true);

    const maxAllowed = 4 * 60 * 60 * 1000;
    if (config.DEFAULT_DURATION > maxAllowed) {
      addToast('error', 'Durée par défaut limitée à 4 heures maximum');
      return;
    }

    const response = await fetch('/api/admin/sessions/config', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ config })
    });

    if (response.ok) {
      addToast('success', 'Configuration sauvegardée avec succès');
      loadActiveSessions();
      if (historiqueVisible) {
        loadSessionHistory();
      }
    } else {
      const error = await response.json();
      addToast('error', error.message || 'Erreur lors de la sauvegarde');
    }
  } catch (error) {
    addToast('error', 'Erreur réseau lors de la sauvegarde');
  } finally {
    setSaving(false);
  }
};

  // 🆕 Fonctions pour les plages horaires
  const handleCreateProfile = () => {
    if (!newProfile.name) {
      addToast('error', 'Le nom du profil est requis');
      return;
    }

    const profile = {
      id: `custom-${Date.now()}`,
      ...newProfile,
      isDefault: false,
      assignedUsers: 0
    };

    setSessionProfiles([...sessionProfiles, profile]);
    setIsCreatingProfile(false);
    setNewProfile({
      name: '',
      description: '',
      type: 'daily',
      startTime: '08:00',
      endTime: '17:00',
      daysOfWeek: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven']
    });
    addToast('success', 'Profil créé avec succès');
  };

  const handleDeleteProfile = (profileId) => {
    setSessionProfiles(sessionProfiles.filter(p => p.id !== profileId));
    setUsers(users.map(u => 
      u.profileId === profileId ? { ...u, profileId: 'permanent' } : u
    ));
    addToast('success', 'Profil supprimé avec succès');
  };

  const handleUserProfileChange = (userId, profileId) => {
    setUsers(users.map(u => 
      u.id === userId ? { ...u, profileId } : u
    ));
    addToast('success', 'Profil utilisateur mis à jour');
  };


return (
    <div className="w-full space-y-6">
      
      {/* La page recevait `onBackToDashboard` sans le déclarer : on y entrait
          sans pouvoir en sortir. */}
      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={Clock}
        titre="Paramétrage des sessions"
        sousTitre="Durées de connexion, plages horaires autorisées et profils utilisateurs"
        actions={<>
          <button
            onClick={loadActiveSessions}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Actualiser</span>
          </button>
          <button
            onClick={saveConfiguration}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#246990' }}
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Sauvegarde…' : 'Sauvegarder'}</span>
          </button>
        </>}
      />


      {/* Les plages horaires et l'historique s'ouvraient par deux boutons a
          bascule dans l'en-tete : rien ne disait, en arrivant, que ces deux
          ecrans existaient. Ce sont des sujets, pas des replis — donc des
          onglets. */}
      <Tabs value={ongletActif} onValueChange={setOngletActif} className="w-full">
        <TabsList className="grid grid-cols-3 mb-6 bg-[#246990]/8 p-1 rounded-xl">
          <TabsTrigger
            value="durees"
            className="data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <Clock size={15} className="mr-2" />
            <span>Durées de session</span>
          </TabsTrigger>
          <TabsTrigger
            value="plages"
            className="data-[state=active]:bg-[#319190] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <CalendarClock size={15} className="mr-2" />
            <span>Plages horaires</span>
          </TabsTrigger>
          <TabsTrigger
            value="historique"
            className="data-[state=active]:bg-[#ae7467] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <History size={15} className="mr-2" />
            <span>Historique</span>
          </TabsTrigger>
        </TabsList>

        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">

        <TabsContent value="plages" className="mt-0 focus-visible:outline-none">
        <CarteReglages
          titre="Plages horaires"
          accent="#319190"
          compteur={<span className="flex items-center gap-2">
              <button
                onClick={() => setTimeRestrictionTab('profiles')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  timeRestrictionTab === 'profiles'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Profils
              </button>
              <button
                onClick={() => setTimeRestrictionTab('assignments')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  timeRestrictionTab === 'assignments'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Affectations
              </button>
              <button
                onClick={() => setTimeRestrictionTab('violations')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  timeRestrictionTab === 'violations'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Violations
              </button>
          </span>}
        >

          {/* TAB: Profils */}
          {timeRestrictionTab === 'profiles' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-gray-600">Configurez les profils de sessions avec plages horaires</p>
                <button
                  onClick={() => setIsCreatingProfile(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Nouveau Profil</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {sessionProfiles.map(profile => (
                  <div key={profile.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {profile.type === 'permanent' ? (
                          <Unlock className="h-6 w-6 text-green-600" />
                        ) : (
                          <Lock className="h-6 w-6 text-orange-600" />
                        )}
                        <div>
                          <h4 className="font-semibold text-gray-900">{profile.name}</h4>
                          <p className="text-sm text-gray-500">{profile.description}</p>
                        </div>
                      </div>
                      {!profile.isDefault && (
                        <button
                          onClick={() => {
                            setConfirmDialogs(prev => ({
                              ...prev,
                              deleteProfile: { isOpen: true, profileId: profile.id }
                            }));
                          }}
                          className="p-1 hover:bg-red-100 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </button>
                      )}
                    </div>

                    {profile.type === 'daily' && (
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2 text-gray-700">
                          <Sun className="h-4 w-4 text-orange-500" />
                          <span>{profile.startTime}</span>
                          <ArrowRight className="h-3 w-3" />
                          <Moon className="h-4 w-4 text-blue-500" />
                          <span>{profile.endTime}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {profile.daysOfWeek.map(day => (
                            <span key={day} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                              {day}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-sm">
                      <span className="text-gray-600">Utilisateurs assignés:</span>
                      <span className="font-semibold">{profile.assignedUsers}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Modal Création Profil */}
              {isCreatingProfile && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">Créer un Profil de Session</h3>
                        <button
                          onClick={() => setIsCreatingProfile(false)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nom du profil</label>
                          <input
                            type="text"
                            value={newProfile.name}
                            onChange={(e) => setNewProfile({ ...newProfile, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Ex: Horaires de nuit"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                          <textarea
                            value={newProfile.description}
                            onChange={(e) => setNewProfile({ ...newProfile, description: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            rows={2}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Type de profil</label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => setNewProfile({ ...newProfile, type: 'permanent' })}
                              className={`p-3 border-2 rounded-lg transition-all ${
                                newProfile.type === 'permanent'
                                  ? 'border-green-500 bg-green-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <Unlock className="h-5 w-5 mx-auto mb-1 text-green-600" />
                              <p className="text-sm font-medium">Permanent (24/7)</p>
                            </button>
                            <button
                              onClick={() => setNewProfile({ ...newProfile, type: 'daily' })}
                              className={`p-3 border-2 rounded-lg transition-all ${
                                newProfile.type === 'daily'
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <Lock className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                              <p className="text-sm font-medium">Journalier</p>
                            </button>
                          </div>
                        </div>

                        {newProfile.type === 'daily' && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Plage horaire</label>
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-2">
                                  <Sun className="h-4 w-4 text-orange-500" />
                                  <input
                                    type="time"
                                    value={newProfile.startTime}
                                    onChange={(e) => setNewProfile({ ...newProfile, startTime: e.target.value })}
                                    className="px-3 py-2 border border-gray-300 rounded-lg"
                                  />
                                </div>
                                <ArrowRight className="h-4 w-4 text-gray-400" />
                                <div className="flex items-center space-x-2">
                                  <Moon className="h-4 w-4 text-blue-500" />
                                  <input
                                    type="time"
                                    value={newProfile.endTime}
                                    onChange={(e) => setNewProfile({ ...newProfile, endTime: e.target.value })}
                                    className="px-3 py-2 border border-gray-300 rounded-lg"
                                  />
                                </div>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Jours autorisés</label>
                              <div className="grid grid-cols-7 gap-2">
                                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                                  <button
                                    key={day}
                                    onClick={() => {
                                      const days = newProfile.daysOfWeek.includes(day)
                                        ? newProfile.daysOfWeek.filter(d => d !== day)
                                        : [...newProfile.daysOfWeek, day];
                                      setNewProfile({ ...newProfile, daysOfWeek: days });
                                    }}
                                    className={`py-2 px-1 rounded-lg text-sm font-medium transition-all ${
                                      newProfile.daysOfWeek.includes(day)
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                  >
                                    {day}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </>
                        )}

                        <div className="flex space-x-3 pt-4">
                          <button
                            onClick={() => setIsCreatingProfile(false)}
                            className="flex-1 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Annuler
                          </button>
                          <button
                            onClick={handleCreateProfile}
                            className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Créer le Profil
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: Affectations */}
          {timeRestrictionTab === 'assignments' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-600">Attribuez un profil de session à chaque utilisateur</p>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="space-y-2">
                {users
                  .filter(u => 
                    u.identifiant.toLowerCase().includes(userSearch.toLowerCase()) ||
                    u.nom.toLowerCase().includes(userSearch.toLowerCase())
                  )
                  .map(user => {
                    const currentProfile = sessionProfiles.find(p => p.id === user.profileId);
                    return (
                      <div key={user.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold">
                            {user.nom?.charAt(0)}{user.prenom?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.identifiant}</p>
                            <p className="text-sm text-gray-500">{user.nom} {user.prenom}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <ChampSelect
                            valeur={user.profileId}
                            onChange={(valeur) => handleUserProfileChange(user.id, valeur)}
                            className="w-56"
                            options={sessionProfiles.map(profile => ({
                              valeur: profile.id,
                              libelle: profile.name,
                            }))}
                          />

                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            currentProfile?.type === 'permanent'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {currentProfile?.type === 'permanent' ? '24/7' : 'Restreint'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* TAB: Violations */}
          {timeRestrictionTab === 'violations' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600">Tentatives de connexion hors plages autorisées</p>
                <div className="flex items-center space-x-2 bg-red-50 px-3 py-1 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-900">{violations.length} violation(s)</span>
                </div>
              </div>

              <div className="space-y-2">
                {violations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Shield className="h-12 w-12 mx-auto mb-2 text-green-300" />
                    <p>Aucune violation détectée</p>
                  </div>
                ) : (
                  violations.map(violation => (
                    <div key={violation.id} className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-red-900">{violation.user}</p>
                          <p className="text-sm text-red-700 mt-1">{violation.reason}</p>
                          <div className="flex items-center space-x-3 mt-2 text-xs text-red-600">
                            <span className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {formatDate(violation.timestamp)}
                            </span>
                            <span className="flex items-center">
                              <span className="mr-1">IP:</span>
                              {violation.ip}
                            </span>
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-red-200 text-red-800 rounded-full text-xs font-medium">
                          Bloqué
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </CarteReglages>
        </TabsContent>


        <TabsContent value="historique" className="mt-0 focus-visible:outline-none">
        <CarteReglages
          titre="Historique des sessions"
          accent="#ae7467"
          compteur={<span className="flex items-center gap-2">
              <ChampSelect
                valeur={historyType}
                onChange={(valeur) => {
                  setHistoryType(valeur);
                  setHistoryPage(1);
                }}
                className="w-52"
                options={[
                  { valeur: 'all', libelle: 'Tout' },
                  { valeur: 'sessions', libelle: 'Sessions seulement' },
                  { valeur: 'admin', libelle: 'Actions admin seulement' },
                ]}
              />
              <button
                onClick={openClearHistoryConfirm}
                className="flex items-center space-x-1 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-md text-sm transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                <span>Vider</span>
              </button>
          </span>}
        >

          <div className="space-y-2">
            {currentHistory.length === 0 ? (
              <p className="text-gray-500 text-sm py-8 text-center">Aucun historique trouvé</p>
            ) : (
              currentHistory.map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg border-l-4 border-blue-400">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        {item.action_type ? (
                          <>
                            <Shield className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">
                              Action Admin: {item.action_type}
                            </span>
                          </>
                        ) : (
                          <>
                            <Users className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-gray-900">
                              {item.identifiant || 'Utilisateur inconnu'}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        {item.action_type ? (
                          <span>
                            Par: {item.admin_identifiant} • Target: {item.target_id} • {formatDate(item.created_at)}
                          </span>
                        ) : (
                          <span>
                            Connexion: {formatDate(item.connection_time)} • 
                            Déconnexion: {formatDate(item.disconnection_time)} • 
                            Durée: {item.duration_minutes}min • 
                            Raison: {item.disconnect_reason}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.status === 'active' && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Actif</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination historique */}
          {historyTotalPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4 mt-4">
              <div className="text-sm text-gray-600">
                Page {historyPage} sur {historyTotalPages} • {filteredHistory.length} élément(s) au total
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                  disabled={historyPage === 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm">{historyPage}</span>
                <button
                  onClick={() => setHistoryPage(Math.min(historyTotalPages, historyPage + 1))}
                  disabled={historyPage === historyTotalPages}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </CarteReglages>
        </TabsContent>
        <TabsContent value="durees" className="mt-0 focus-visible:outline-none">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Configuration principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Durées de session */}
          <CarteReglages titre="Durées de Session" accent="#246990">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée par défaut
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={formatDurationForInput(config.DEFAULT_DURATION).value}
                    onChange={(e) => {
                      const currentFormat = formatDurationForInput(config.DEFAULT_DURATION);
                      let inputValue = e.target.value;
                      
                      if (currentFormat.unit === 'hours' && inputValue.includes(':')) {
                        const parts = inputValue.split(':');
                        if (parts.length === 2) {
                          const minutes = parts[1];
                          if (minutes && parseInt(minutes) >= 60) {
                            inputValue = `${parts[0]}:59`;
                          }
                        }
                      }
                      
                      const newMinutes = parseDurationInput(inputValue, currentFormat.unit);
                      setConfig({
                        ...config,
                        DEFAULT_DURATION: minutesToMs(newMinutes)
                      });
                    }}
                    onKeyDown={(e) => {
                      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
                      const allowedChars = /[0-9:\.]/;
                      
                      if (!allowedKeys.includes(e.key) && !allowedChars.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    placeholder="Ex: 2:30 ou 150"
                  />
                  <ChampSelect
                    valeur={formatDurationForInput(config.DEFAULT_DURATION).unit}
                    onChange={(unite) => {
                      const currentValue = formatDurationForInput(config.DEFAULT_DURATION).value;
                      setConfig({ ...config, DEFAULT_DURATION: minutesToMs(parseDurationInput(currentValue, unite)) });
                    }}
                    className="w-24"
                    options={[
                      { valeur: "minutes", libelle: "min" },
                      { valeur: "hours", libelle: "h" },
                    ]}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Durée initiale des nouvelles sessions (Ex: 2:30 = 2h30min)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée d'extension
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={formatDurationForInput(config.EXTENSION_DURATION).value}
                    onChange={(e) => {
                      const currentFormat = formatDurationForInput(config.EXTENSION_DURATION);
                      const newMinutes = parseDurationInput(e.target.value, currentFormat.unit);
                      setConfig({
                        ...config,
                        EXTENSION_DURATION: minutesToMs(newMinutes)
                      });
                    }}
                    onKeyDown={(e) => {
                      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
                      const allowedChars = /[0-9:\.]/;
                      
                      if (!allowedKeys.includes(e.key) && !allowedChars.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    placeholder="Ex: 1:00 ou 60"
                  />
                  <ChampSelect
                    valeur={formatDurationForInput(config.EXTENSION_DURATION).unit}
                    onChange={(unite) => {
                      const currentValue = formatDurationForInput(config.EXTENSION_DURATION).value;
                      setConfig({ ...config, EXTENSION_DURATION: minutesToMs(parseDurationInput(currentValue, unite)) });
                    }}
                    className="w-24"
                    options={[
                      { valeur: "minutes", libelle: "min" },
                      { valeur: "hours", libelle: "h" },
                    ]}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Temps ajouté lors d'une prolongation
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée max quotidienne
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={formatDurationForInput(config.MAX_DAILY_DURATION).value}
                    onChange={(e) => {
                      const currentFormat = formatDurationForInput(config.MAX_DAILY_DURATION);
                      const newMinutes = parseDurationInput(e.target.value, currentFormat.unit);
                      setConfig({
                        ...config,
                        MAX_DAILY_DURATION: minutesToMs(newMinutes)
                      });
                    }}
                    onKeyDown={(e) => {
                      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
                      const allowedChars = /[0-9:\.]/;
                      
                      if (!allowedKeys.includes(e.key) && !allowedChars.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    placeholder="Ex: 8:00 ou 480"
                  />
                  <ChampSelect
                    valeur={formatDurationForInput(config.MAX_DAILY_DURATION).unit}
                    onChange={(unite) => {
                      const currentValue = formatDurationForInput(config.MAX_DAILY_DURATION).value;
                      setConfig({ ...config, MAX_DAILY_DURATION: minutesToMs(parseDurationInput(currentValue, unite)) });
                    }}
                    className="w-24"
                    options={[
                      { valeur: "minutes", libelle: "min" },
                      { valeur: "hours", libelle: "h" },
                    ]}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Durée maximale avec extensions (Ex: 8:00 = 8h00min)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Extensions par jour
                </label>
                <input
                  type="number"
                  value={config.MAX_EXTENSIONS_PER_DAY}
                  onChange={(e) => setConfig({
                    ...config,
                    MAX_EXTENSIONS_PER_DAY: parseInt(e.target.value) || 0
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  min="0"
                  max="10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nombre d'extensions autorisées
                </p>
              </div>
            </div>
          </CarteReglages>


{/* Alertes */}
          <CarteReglages titre="Alertes d'Expiration" accent="#246990">

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alerte Warning
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={msToMinutes(config.ALERT_TIMES.WARNING)}
                    onChange={(e) => setConfig({
                      ...config,
                      ALERT_TIMES: {
                        ...config.ALERT_TIMES,
                        WARNING: minutesToMs(parseInt(e.target.value) || 0)
                      }
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    min="1"
                    max="30"
                  />
                  <span className="text-sm text-gray-500">min</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alerte Urgent
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={msToMinutes(config.ALERT_TIMES.URGENT)}
                    onChange={(e) => setConfig({
                      ...config,
                      ALERT_TIMES: {
                        ...config.ALERT_TIMES,
                        URGENT: minutesToMs(parseInt(e.target.value) || 0)
                      }
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    min="1"
                    max="10"
                  />
                  <span className="text-sm text-gray-500">min</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alerte Critical
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={Math.floor(config.ALERT_TIMES.CRITICAL / 1000)}
                    onChange={(e) => setConfig({
                      ...config,
                      ALERT_TIMES: {
                        ...config.ALERT_TIMES,
                        CRITICAL: (parseInt(e.target.value) || 0) * 1000
                      }
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    min="10"
                    max="300"
                  />
                  <span className="text-sm text-gray-500">sec</span>
                </div>
              </div>
            </div>
          </CarteReglages>

        </div>

        {/* Sidebar - Statistiques et Sessions */}
        <div className="space-y-6">
          {/* Statistiques */}
          {stats && (
            <CarteReglages
              titre="Sessions actives"
              accent="#246990"
            >

              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Sessions actives</span>
                  <span className="font-semibold" style={{ color: '#246990' }}>
                    {stats.totalActiveSessions}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Utilisateurs uniques</span>
                  <span className="font-semibold" style={{ color: '#246990' }}>
                    {stats.uniqueUsers}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Multi-sessions</span>
                  <span className="font-semibold" style={{ color: '#246990' }}>
                    {stats.multiSessionUsers}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600">Sessions/utilisateur</span>
                  <span className="font-semibold" style={{ color: '#246990' }}>
                    {typeof stats.averageSessionsPerUser === 'number' ? stats.averageSessionsPerUser.toFixed(1) : '0.0'}
                  </span>
                </div>
              </div>
            </CarteReglages>
          )}

          {/* Sessions actives enrichies */}
          <CarteReglages
            titre="Profils de session"
            accent="#7c3aed"
            compteur={<span className="flex items-center gap-2">
                <button
                  onClick={loadActiveSessions}
                  disabled={loading}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Actualiser"
                >
                  <RefreshCw className={`h-4 w-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <span className="text-sm text-gray-500">
                  {sessions.length} session(s)
                </span>
            </span>}
          >

            {/* Liste des sessions avec pagination et enrichissements */}
            <div className="space-y-2 mb-4">
              {sessions.length === 0 ? (
                <p className="text-gray-500 text-sm py-8 text-center">Aucune session active</p>
              ) : (
                currentSessions.map((session) => {
                  const timeInfo = formatTimeRemaining(session.timeLeftMinutes);
                  return (
                    <div key={session.sessionId} className="p-3 bg-gray-50 rounded-lg border-l-4" 
                         style={{ 
                           borderLeftColor: timeInfo.urgency === 'critical' ? '#dc2626' : 
                                          timeInfo.urgency === 'warning' ? '#ea580c' : '#16a34a'
                         }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {session.user.identifiant}
                            </p>
                            <button
                              onClick={() => openDeleteSessionConfirm(session.sessionId)}
                              className="p-1 hover:bg-red-100 rounded text-red-600 transition-colors"
                              title="Supprimer cette session"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-500">
                            {session.user.nom} {session.user.prenom}
                          </p>
                          {/* Affichage de l'heure de connexion */}
                          {session.metadata?.connectionTime && (
                            <div className="flex items-center mt-1 space-x-1">
                              <Calendar className="h-3 w-3 text-blue-500" />
                              <span className="text-xs text-blue-600">
                                Connecté à {formatDate(session.metadata.connectionTime)}
                              </span>
                            </div>
                          )}
                          {timeInfo.urgency === 'critical' && (
                            <div className="flex items-center mt-1">
                              <AlertTriangle className="h-3 w-3 text-red-500 mr-1" />
                              <span className="text-xs text-red-600 font-medium">
                                Expiration imminente
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${timeInfo.color}`}>
                            {timeInfo.text}
                          </p>
                          <p className="text-xs text-gray-500">
                            {timeInfo.description}
                          </p>
                          {timeInfo.urgency === 'critical' && (
                            <div className="mt-1">
                              <div className="w-16 h-1 bg-red-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-red-500 rounded-full animate-pulse"
                                  style={{ width: `${Math.max(10, (session.timeLeftMinutes / 5) * 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t pt-4">
                <div className="text-sm text-gray-600">
                  Page {currentPage} sur {totalPages} • {sessions.length} session(s) au total
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  
                  {/* Numéros de pages */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`w-8 h-8 rounded text-sm font-medium ${
                          pageNum === currentPage
                            ? 'text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        style={pageNum === currentPage ? { backgroundColor: '#246990' } : {}}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </CarteReglages>

        </div>
      </div>

      {/* Résumé de configuration */}
      <CarteReglages titre="Résumé de la configuration" accent="#246990">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Session par défaut:</span>
            <div className="font-semibold" style={{ color: '#246990' }}>
              {formatTime(msToMinutes(config.DEFAULT_DURATION))}
            </div>
          </div>
          <div>
            <span className="text-gray-600">Extension:</span>
            <div className="font-semibold" style={{ color: '#246990' }}>
              {formatTime(msToMinutes(config.EXTENSION_DURATION))}
            </div>
          </div>
          <div>
            <span className="text-gray-600">Maximum quotidien:</span>
            <div className="font-semibold" style={{ color: '#246990' }}>
              {formatTime(msToMinutes(config.MAX_DAILY_DURATION))}
            </div>
          </div>
          <div>
            <span className="text-gray-600">Extensions max:</span>
            <div className="font-semibold" style={{ color: '#246990' }}>
              {config.MAX_EXTENSIONS_PER_DAY}
            </div>
          </div>
        </div>
      </CarteReglages>
        </TabsContent>
        </div>
      </Tabs>

      {/* Dialogues de confirmation */}
      <ConfirmDialog
        isOpen={confirmDialogs.deleteSession.isOpen}
        onClose={closeDeleteSessionConfirm}
        onConfirm={handleDeleteSession}
        title="Supprimer la session"
        description="Êtes-vous sûr de vouloir supprimer cette session ? L'utilisateur sera déconnecté immédiatement."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
      />

      <ConfirmDialog
        isOpen={confirmDialogs.clearHistory.isOpen}
        onClose={closeClearHistoryConfirm}
        onConfirm={handleClearHistory}
        title="Vider l'historique"
        description="Êtes-vous sûr de vouloir vider l'historique ? Cette action est irréversible et supprimera définitivement toutes les données d'historique."
        confirmText="Vider l'historique"
        cancelText="Annuler"
        type="error"
      />

      <ConfirmDialog
        isOpen={confirmDialogs.deleteProfile.isOpen}
        onClose={() => setConfirmDialogs(prev => ({ ...prev, deleteProfile: { isOpen: false, profileId: null } }))}
        onConfirm={() => {
          handleDeleteProfile(confirmDialogs.deleteProfile.profileId);
          setConfirmDialogs(prev => ({ ...prev, deleteProfile: { isOpen: false, profileId: null } }));
        }}
        title="Supprimer le profil"
        description="Les utilisateurs assignés seront basculés en accès permanent. Continuer ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        type="error"
      />

      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            type={toast.type}
            message={toast.message}
            onClose={() => removeToast(toast.id)}
            autoClose={5000}
          />
        ))}
      </div>

    </div>
  );
};

export default SessionConfigDashboard;